import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { KeyRound, Pencil, Plus, ShieldOff, Trash2 } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import { LoadingState, ErrorState } from '@/components/ui/states';
import { apiMessage, fullName, pruneParams, useDebounced } from '../helpers';
import { DEVICE_TYPES, type Branch } from '../types';
import DeviceFormDialog, {
  PairingCodeDialog,
  type IssuedPairing,
  type PairedDevice,
} from './DeviceFormDialog';
import ConfirmDialog from './ConfirmDialog';

/**
 * Device registry (§13).
 *
 * This screen used to identify a device by its fingerprint — the value shown
 * under its name, the value typed into the add form. That was backwards twice
 * over: the fingerprint is generated by the app on the device, so an admin
 * adding a tablet could not know it, and it means nothing to the person reading
 * the table. Identity here is now name / type / branch / last seen / status, and
 * the fingerprint has moved into the edit dialog's diagnostics with a sentence
 * saying what it is.
 *
 * The three states a row can be in are also kept apart. "Awaiting pairing" (a
 * code is out, no device has claimed it), "code expired" and "revoked" all used
 * to render as the same red `isActive: false` badge, which made a tablet nobody
 * had unboxed yet look like a security incident.
 */
export default function DevicesPanel() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();

  const [search, setSearch] = useState('');
  const [branchId, setBranchId] = useState('');
  const [type, setType] = useState('');
  const [pairing, setPairing] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const debouncedSearch = useDebounced(search);

  const params = useMemo(
    () =>
      pruneParams({
        limit: 200,
        search: debouncedSearch,
        branchId,
        type,
        pairing,
        isActive: activeFilter,
      }),
    [debouncedSearch, branchId, type, pairing, activeFilter],
  );

  const devicesQuery = useApi<unknown>('/devices', params);
  const branchesQuery = useApi<unknown>('/branches', { limit: 200 });
  const devices = rowsOf<PairedDevice>(devicesQuery.data).rows;
  const branches = rowsOf<Branch>(branchesQuery.data).rows;

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PairedDevice | null>(null);
  const [issued, setIssued] = useState<IssuedPairing | null>(null);
  const [repairing, setRepairing] = useState<PairedDevice | null>(null);
  const [revoking, setRevoking] = useState<PairedDevice | null>(null);
  const [deleting, setDeleting] = useState<PairedDevice | null>(null);

  const canRegister = can('devices.register');
  const canRevoke = can('devices.revoke');

  /**
   * Re-pairing is what a wiped, reset or replaced device needs: its identifier
   * changed, so the row's old binding is dead. Issuing a fresh code releases the
   * binding server-side and keeps the row — its name, branch and history — which
   * is the alternative to an admin deleting and recreating it by hand.
   */
  const repair = async () => {
    if (!repairing) return;
    try {
      const { data } = await api.post<IssuedPairing>(`/devices/${repairing.id}/pairing`);
      devicesQuery.reload();
      setIssued(data);
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.actionFailed')));
    }
  };

  const revoke = async () => {
    if (!revoking) return;
    try {
      await api.post(`/devices/${revoking.id}/revoke`);
      toast.success(t('settings.devices.revoked'));
      devicesQuery.reload();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.actionFailed')));
    }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/devices/${deleting.id}`);
      toast.success(t('settings.devices.deleted'));
      devicesQuery.reload();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.actionFailed')));
    }
  };

  /**
   * Pairing state outranks `isActive` in the badge, because it is the more
   * specific fact. A pending row is inactive too, but calling it "revoked" is
   * simply wrong and sends the admin looking for a problem that does not exist.
   */
  const statusBadge = (device: PairedDevice) => {
    const state = device.pairing?.status ?? 'PAIRED';
    if (state === 'PENDING') {
      return <Badge tone="warning">{t('settings.devices.pendingStatus')}</Badge>;
    }
    if (state === 'EXPIRED') {
      return <Badge tone="danger">{t('settings.devices.expiredStatus')}</Badge>;
    }
    return (
      <Badge tone={device.isActive ? 'success' : 'danger'}>
        {device.isActive ? t('settings.common.active') : t('settings.devices.revokedStatus')}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[12rem] flex-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('settings.devices.searchPlaceholder')}
            aria-label={t('common.search')}
          />
        </div>
        <Select
          className="w-auto min-w-[10rem]"
          value={branchId}
          onChange={(e) => setBranchId(e.target.value)}
          aria-label={t('settings.devices.branch')}
        >
          <option value="">{t('settings.devices.allBranches')}</option>
          {branches.map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.name}
            </option>
          ))}
        </Select>
        <Select
          className="w-auto min-w-[10rem]"
          value={type}
          onChange={(e) => setType(e.target.value)}
          aria-label={t('settings.devices.type')}
        >
          <option value="">{t('settings.devices.allTypes')}</option>
          {DEVICE_TYPES.map((option) => (
            <option key={option} value={option}>
              {t(`settings.devices.types.${option}`)}
            </option>
          ))}
        </Select>
        <Select
          className="w-auto min-w-[10rem]"
          value={pairing}
          onChange={(e) => setPairing(e.target.value)}
          aria-label={t('settings.devices.pairing')}
        >
          <option value="">{t('settings.devices.allPairing')}</option>
          <option value="PENDING">{t('settings.devices.pendingFilter')}</option>
          <option value="PAIRED">{t('settings.devices.pairedFilter')}</option>
        </Select>
        <Select
          className="w-auto min-w-[9rem]"
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
          aria-label={t('settings.common.status')}
        >
          <option value="">{t('settings.common.allStatuses')}</option>
          <option value="true">{t('settings.common.active')}</option>
          <option value="false">{t('settings.devices.revokedStatus')}</option>
        </Select>

        {canRegister && (
          <Button
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            {t('settings.devices.add')}
          </Button>
        )}
      </div>

      {devicesQuery.loading ? (
        <LoadingState label={t('common.loading')} />
      ) : devicesQuery.error ? (
        <ErrorState message={devicesQuery.error} onRetry={devicesQuery.reload} />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('settings.devices.name')}</TH>
                <TH>{t('settings.devices.type')}</TH>
                <TH>{t('settings.devices.branch')}</TH>
                <TH>{t('settings.devices.boundUser')}</TH>
                <TH>{t('settings.devices.lastSeen')}</TH>
                <TH>{t('settings.common.status')}</TH>
                <TH className="text-end">{t('settings.common.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {devices.length === 0 ? (
                <TableEmpty colSpan={7}>{t('settings.devices.empty')}</TableEmpty>
              ) : (
                devices.map((device) => {
                  const state = device.pairing?.status ?? 'PAIRED';
                  const isPending = state !== 'PAIRED';
                  return (
                    <TR key={device.id}>
                      <TD>
                        <span className="font-medium">{device.name}</span>
                        {isPending && (
                          <p className="text-xs text-ink-muted">
                            {state === 'EXPIRED'
                              ? t('settings.devices.pairingCodeExpired')
                              : device.pairing?.expiresAt
                                ? t('settings.devices.pairingCodeExpiresAt', {
                                    time: formatDateTime(device.pairing.expiresAt, i18n.language),
                                  })
                                : t('settings.devices.notPairedYet')}
                          </p>
                        )}
                      </TD>
                      <TD>
                        <Badge tone="neutral">{t(`settings.devices.types.${device.type}`)}</Badge>
                      </TD>
                      <TD>{device.branch?.name ?? t('settings.common.none')}</TD>
                      <TD>{device.user ? fullName(device.user) : t('settings.common.none')}</TD>
                      <TD className="text-ink-muted">
                        {device.lastSeenAt
                          ? formatDateTime(device.lastSeenAt, i18n.language)
                          : t('settings.common.never')}
                      </TD>
                      <TD>{statusBadge(device)}</TD>
                      <TD>
                        <div className="flex justify-end gap-1">
                          {canRegister && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title={
                                isPending
                                  ? t('settings.devices.newCode')
                                  : t('settings.devices.repair')
                              }
                              onClick={() => setRepairing(device)}
                            >
                              <KeyRound className="h-4 w-4 text-accent" />
                            </Button>
                          )}
                          {canRegister && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title={t('common.edit')}
                              onClick={() => {
                                setEditing(device);
                                setFormOpen(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canRevoke && device.isActive && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title={t('settings.devices.revoke')}
                              onClick={() => setRevoking(device)}
                            >
                              <ShieldOff className="h-4 w-4 text-amber-600" />
                            </Button>
                          )}
                          {canRevoke && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title={t('common.delete')}
                              onClick={() => setDeleting(device)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                            </Button>
                          )}
                        </div>
                      </TD>
                    </TR>
                  );
                })
              )}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <DeviceFormDialog
        open={formOpen}
        device={editing}
        onClose={() => setFormOpen(false)}
        onSaved={devicesQuery.reload}
      />

      <PairingCodeDialog issued={issued} onClose={() => setIssued(null)} />

      <ConfirmDialog
        open={repairing !== null}
        title={
          repairing?.pairing?.status && repairing.pairing.status !== 'PAIRED'
            ? t('settings.devices.newCode')
            : t('settings.devices.repairTitle')
        }
        message={t('settings.devices.repairConfirm', { name: repairing?.name ?? '' })}
        confirmLabel={t('settings.devices.newCode')}
        onConfirm={repair}
        onClose={() => setRepairing(null)}
      />

      <ConfirmDialog
        open={revoking !== null}
        title={t('settings.devices.revoke')}
        message={t('settings.devices.revokeConfirm', { name: revoking?.name ?? '' })}
        confirmLabel={t('settings.devices.revoke')}
        onConfirm={revoke}
        onClose={() => setRevoking(null)}
      />

      <ConfirmDialog
        open={deleting !== null}
        title={t('settings.devices.deleteTitle')}
        message={t('settings.devices.deleteConfirm', { name: deleting?.name ?? '' })}
        confirmLabel={t('common.delete')}
        onConfirm={remove}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}
